<template>
	<section v-if="authStore.mfa.backupEmail" class="mfa-login-process">
		<CMultifactorInput
			ref="mfaInput"
			:is-validated="validated"
			:is-loading="loading"
			autofocus
			input-classes="mt-3"
			label="Enter your two-factor authenticator app code."
			v-on="{ complete: !authStore.mfa.isSocial ? login : validateCode }"
		/>
		<CDeprecatedCheckbox
			v-if="authStore.mfa.isSocial"
			id="remember-device"
			v-model="rememberDevice"
			class="mb-3 sz-2 flex flex--center"
			label="Remember this Device"
		/>
		<CStatusButton
			v-if="authStore.mfa.isSocial"
			class="button button--primary sz-4 my-3"
			title="Log In"
			type="submit"
			:status="socialStatus"
			:disabled="!validated"
			@click="login(mfaCode)"
		>
			Log In
		</CStatusButton>
		<div class="sz-2">
			<p class="text-center m-0">
				Can't access your authenticator?
			</p>
			<button class="button--no-style text-center underline w-100" @click="sendMfaEmail">
				Send a two-factor code to {{ authStore.mfa.backupEmail }}
			</button>
		</div>
	</section>
	<section v-else>
		<EnableMultifactor
			:last-user="authStore.lastUser"
			:token="authStore.mfa.multifactorToken"
			:is-loading="loading"
			:remember-me="rememberMe"
			:encrypted-auth-method="authStore.mfa.encryptedAuthMethod"
			@complete="login"
		/>
	</section>
</template>

<script>
	import EnableMultifactor from 'src/views/auth/EnableMultifactor.vue';
	import CStatusButton from '@cognitoforms/shared-components/src/components/interaction/StatusButton.vue';
	import CDeprecatedCheckbox from '@cognitoforms/shared-components/src/components/form-controls/DeprecatedCheckbox.vue';
	import notify from '@cognitoforms/shared-components/src/utilities/notify';
	import CMultifactorInput from 'src/components/form-controls/MultifactorInput.vue';
	import { authStore } from 'src/stores/auth-store';
	import { multifactorLogin, validateMultifactorCode, sendBackupMultifactorCode } from '@cognitoforms/api/services/authentication';

	export default {
		components: {
			EnableMultifactor,
			CStatusButton,
			CDeprecatedCheckbox,
			CMultifactorInput
		},
		props: {
			rememberMe: { type: Boolean, default: false },
			inviteCode: { type: String, default: null },
			authenticationProvider: { type: String, default: null }
		},
		data() {
			return {
				loading: false,
				rememberDevice: false,
				validated: false,
				mfaCode: '',
				socialStatus: 'default',
				authStore
			};
		},
		mounted() {
			// Check for an authentication provider other than cognito to show remember me
			if (this.authenticationProvider !== 'cognito') {
				this.authStore.mfa.isSocial = true;
			}
		},
		methods: {
			login(code) {
				this.loading = true;
				this.socialStatus = 'validating';
				multifactorLogin(
					this.authStore.mfa.multifactorToken,
					code,
					this.rememberMe,
					this.rememberDevice,
					this.inviteCode,
					this.verificationCode,
					this.requiredOrganization,
					this.requiredUser,
					this.authStore.mfa.encryptedAuthMethod
				).then(res => {
					const handleAuthError = () => {
						this.loading = false;
						this.validated = false;
						this.socialStatus = 'default';
						this.authStore.mfa.multifactorToken = null;
						this.resetButtonStatuses();
					};

					if (res.Succeeded) {
						this.validated = true;
						this.loading = false;
						setTimeout(() => {
							return window.location.assign(res.ReturnUrl);
						}, 500);
					}
					else if (res.RedirectUri)
						return window.location.assign(res.RedirectUri);
					else if (res.InvalidOrg) {
						handleAuthError();
						this.showInvalidOrgMessage = true;
					}
					else if (res.InvalidUser) {
						handleAuthError();
						this.showInvalidUserMessage = true;
					}
					else {
						this.loading = false;
						this.validated = false;
						this.socialStatus = 'default';
					}
				}).catch(error => {
					this.loading = false;
					this.validated = false;
					this.socialStatus = 'default';

					if (error.statusCode === 429) {
						this.authStore.mfa.multifactorToken = null;
						this.authStore.mfa.locked = true;
					}

					this.resetButtonStatuses();
				});
			},
			validateCode(code) {
				this.loading = true;
				this.mfaCode = code;
				validateMultifactorCode(this.authStore.mfa.multifactorToken, code).then(response => {
					this.validated = response.Succeeded;
					this.loading = false;
				}).catch(error => {
					if (error.statusCode === 429) {
						this.authStore.mfa.multifactorToken = null;
						this.authStore.mfa.locked = true;
					}
					this.resetButtonStatuses();
					this.validated = false;
					this.loading = false;
				});
			},
			resetButtonStatuses() {
				this.authStore.emailSubmitStatus = this.authStore.googleSubmitStatus = this.authStore.facebookSubmitStatus = this.authStore.microsoftSubmitStatus = 'default';
			},
			sendMfaEmail() {
				sendBackupMultifactorCode(this.authStore.mfa.multifactorToken).then(() => {
					notify('We\'re sending you a two-factor code now.', 'success');
					// set focus on the mfa input field
					this.$refs.mfaInput.$el.querySelector('input').focus();
				}).catch(() => {
					notify('An error has occurred.', 'error');
				});
			}
		}
	};
</script>